import React, { useState } from 'react';
import moment from 'moment-timezone';
import useLoading from 'library/hooks/useLoading';
import { Checkbox, Modal, PillButton, Row, Stack, Text } from 'library/ui';
import { formatShortWeekdayMonthDay } from 'shared/Dates';

import JobTimePicker from '../../../../forms/components/pages/DateAndTimePage/JobTimePicker';
import { createCenterInitiatedAppointmentTimeChangeRequest, updateBusinessAppointment } from './businessAppointmentApi';

import type { IShift } from 'models';
import type { ILeanShift } from '../../BusinessJob/models';
import type { JobTimes } from 'parent-portal/forms/JobRequestTypes';

export default function EditModal({
    open,
    onClose,
    shift,
    providerName,
    refresh,
    timezone,
}: {
    open: boolean;
    onClose: () => void;
    shift: IShift | ILeanShift;
    providerName?: string;
    refresh: () => void;
    timezone: string;
}) {
    const { loading, setLoading } = useLoading();
    const toJobTime = (date: string) => (timezone ? moment.tz(date, timezone) : moment(date));
    const [jobTimesValue, setJobTimesValue] = useState<JobTimes>(() => ({
        start: toJobTime(shift.start_date),
        end: toJobTime(shift.end_date),
        slots: 1,
        timezone,
    }));
    const [jobTimesValid, setJobTimesValid] = useState(false);
    const [removeProviderValue, setRemoveProviderValue] = useState(false);

    const jobTimes = {
        value: jobTimesValue,
        setValue: setJobTimesValue,
        isValid: jobTimesValid,
        setValid: setJobTimesValid,
    };
    const removeProvider = { value: removeProviderValue, setValue: setRemoveProviderValue };

    function submit() {
        setLoading(true);
        const originalStart = toJobTime(shift.start_date);
        const originalEnd = toJobTime(shift.end_date);

        const newStart = toJobTime(shift.start_date);
        const newEnd = toJobTime(shift.end_date);
        newStart
            .hour(jobTimes.value.start?.hour() ?? originalStart.hour())
            .minute(jobTimes.value.start?.minute() ?? originalStart.minute());
        newEnd
            .hour(jobTimes.value.end?.hour() ?? originalEnd.hour())
            .minute(jobTimes.value.end?.minute() ?? originalEnd.minute());

        if (originalStart.isSame(newStart) && originalEnd.isSame(newEnd)) {
            setLoading(false);
            onClose();
        } else if (!providerName) {
            updateBusinessAppointment(shift.id, {
                start_date: newStart.toISOString(),
                end_date: newEnd.toISOString(),
            })
                .then(refresh)
                .finally(() => {
                    setLoading(false);
                    onClose();
                });
        } else {
            createCenterInitiatedAppointmentTimeChangeRequest({
                appointmentId: shift.id,
                startTime: newStart,
                endTime: newEnd,
                onDecline: removeProvider.value ? 'remove_provider' : 'keep_provider',
            })
                .then(refresh)
                .finally(() => {
                    setLoading(false);
                    onClose();
                });
        }
    }

    const mainText = providerName
        ? [
              `We will inform ${providerName} of updates to the shift times.`,
              'If there are any issues with this reschedule, we will update you as soon as possible.',
          ].join(' ')
        : [
              'We will attempt to fill this shift with the updated times.',
              'If you requested to book multiple providers for this shift, all unfilled shifts will be updated.',
          ].join(' ');

    const disclaimerText = providerName
        ? [
              `Note: if ${providerName} does not respond by the shift start time,`,
              `the times will be kept as the original shift times and ${providerName} will remain on the shift.`,
          ].join(' ')
        : false;

    if (!open) {
        return null;
    }

    return (
        <Modal
            title={`Need to update the times for ${formatShortWeekdayMonthDay(shift.start_date)}?`}
            onClose={onClose}
        >
            <Stack gap="md">
                <Text>{mainText}</Text>
                {disclaimerText && <Text>{disclaimerText}</Text>}
                <JobTimePicker jobTimes={jobTimes} startDate={toJobTime(shift.start_date)} allowPastMidnight={false} />
                {!!providerName && (
                    <Checkbox
                        label={`Remove ${providerName} if they cannot work the new times.`}
                        checked={removeProvider.value}
                        onChange={(checked) => removeProvider.setValue(checked)}
                    />
                )}
                <Row justify="end" fullWidth>
                    <PillButton disabled={loading} onClick={submit}>
                        {loading ? 'Submitting…' : 'Submit'}
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
